import React, { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";

import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  EditOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  StarFilled,
  SwapOutlined,
} from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import { ROUTE_PATHS } from "../../routeConstants";
import {
  calculatePricingAndLink,
  createSaleItem,
  fetchFeatureMangoConfig,
  fetchSaleItems,
  reorderSaleItems,
  updateSaleItem,
} from "../api";

const AVAILABLE_FEATURES = [
  { value: "AI_VERSE", label: "AI_VERSE" },
  { value: "OTO_ENTERPRISE", label: "OTO_ENTERPRISE" },
];

// Draggable Row Component
const DraggableRow = ({
  children,
  "data-row-key": dataRowKey,
  style: rowStyle,
  ...props
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dataRowKey,
  });

  const style = {
    ...rowStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
          background: "#fafafa",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        }
      : {}),
  };

  return (
    <tr
      {...props}
      data-row-key={dataRowKey}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {React.Children.map(children, (child) => {
        if (child.key === "drag-handle") {
          return React.cloneElement(child, {
            children: (
              <HolderOutlined
                style={{
                  cursor: isDragging ? "grabbing" : "grab",
                  color: "#999",
                  fontSize: 16,
                }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

DraggableRow.propTypes = {
  children: PropTypes.node,
  "data-row-key": PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};

DraggableRow.defaultProps = {
  children: null,
  "data-row-key": "",
  style: {},
};

const { Title } = Typography;

const globalPlans = [
  {
    key: 1,
    name: "Free",
    value: "free",
    platform: "tagmango",
    tier: "tier1",
  },
  {
    key: 2,
    name: "Freedom Basic",
    value: "freedom",
    platform: "freedom",
    tier: "tier1",
  },
  {
    key: 3,
    name: "Freedom Pro",
    value: "freedom",
    platform: "freedom",
    tier: "tier2",
  },
  {
    key: 4,
    name: "Freedom Advanced",
    value: "freedom",
    platform: "freedom",
    tier: "tier3",
  },
  {
    key: 5,
    name: "Enterprise Basic",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier1",
  },
  {
    key: 6,
    name: "Enterprise Pro",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier2",
  },
  {
    key: 7,
    name: "Enterprise Advanced",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier3",
  },
  {
    key: 8,
    name: "Enterprise Ultimate",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier4",
  },
];

// Additional FROM options offered only when creating/editing a feature item.
// Each maps to a null-tier (or null platform) combination so the payload can
// target broader user segments.
const FEATURE_FROM_PLAN_EXTRAS = [
  {
    key: "all",
    name: "All",
    value: "all",
    platform: null,
    tier: null,
  },
  {
    key: "freedom-all",
    name: "Freedom All",
    value: "freedom-all",
    platform: "freedom",
    tier: null,
  },
  {
    key: "enterprise-all",
    name: "Enterprise All",
    value: "enterprise-all",
    platform: "enterprise",
    tier: null,
  },
];

const itemNameOptions = [
  "Freedom Setup | Pro Monthly",
  "Freedom Setup | Advanced Monthly",
  "Freedom Setup | Ultimate Monthly",
  "Freedom Setup | Pro Annual",
  "Freedom Setup | Advanced Annual",
  "Freedom Setup | Ultimate Annual",
  "Enterprise Setup | Pro Monthly",
  "Enterprise Setup | Advanced Monthly",
  "Enterprise Setup | Ultimate Monthly",
  "Enterprise Setup | Pro Annual",
  "Enterprise Setup | Advanced Annual",
  "Enterprise Setup | Ultimate Annual",
  "Freedom Setup Basic",
  "Enterprise Setup Basic",
].map((name) => ({ label: name, value: name }));

export const globalMapTierAndPlan = (type) => {
  switch (type) {
    case "tier1":
      return "Basic";
    case "tier2":
      return "Pro";
    case "tier3":
      return "Advanced";
    case "tier4":
      return "Ultimate";
    case "free":
      return "tagmango";
    default:
      return type;
  }
};

const ManageSale = () => {
  const [loader, setLoader] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [originalValues, setOriginalValues] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [calculatedBasePrice, setCalculatedBasePrice] = useState(null);
  const [calculatedMangoLink, setCalculatedMangoLink] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [fromPlan, setFromPlan] = useState(null);
  const [toPlan, setToPlan] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isLifeTimeValidity, setIsLifeTimeValidity] = useState(false);
  const [showNoMangoModal, setShowNoMangoModal] = useState(false);
  const [isCreateMangoDeleted, setIsCreateMangoDeleted] = useState(false);
  const [isFeatureItem, setIsFeatureItem] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [mangoConfigOptions, setMangoConfigOptions] = useState([]);
  const [showConfigPicker, setShowConfigPicker] = useState(false);
  const [pickedConfigId, setPickedConfigId] = useState(null);
  const [saleMangoConfigId, setSaleMangoConfigId] = useState(null);
  const [form] = Form.useForm();
  const { saleId } = useParams();
  const [saleData, setSalesData] = useState(null);
  const searchParams = new URLSearchParams(window.location.search);

  // Fetch calculation when both userTypeFrom and planTypeTo are selected
  const handleCalculation = async (userTypeFrom, planTypeTo, options = {}) => {
    const { isEdit = false } = options;
    if (!userTypeFrom || !planTypeTo) {
      setCalculatedBasePrice(null);
      setCalculatedMangoLink(null);
      return;
    }
    const formFromPlanValue = form.getFieldValue("userTypeFrom");
    const fromPlanObj =
      globalPlans.find((p) => p.key === formFromPlanValue) ||
      globalPlans.find((p) => p.value === userTypeFrom);

    const formToPlanValue = form.getFieldValue("planTypeTo");
    const toPlanObj =
      globalPlans.find((p) => formToPlanValue === p.key) ??
      globalPlans.find(
        (p) =>
          p.value === planTypeTo || form.getFieldValue("planTypeTo") === p.value
      );
    console.log({ fromPlanObj, toPlanObj });
    if (!fromPlanObj || !toPlanObj) {
      console.error("From plan or to plan not found", {
        fromPlanObj,
        toPlanObj,
        fromPlan,
        toPlan,
        toPlanValueFromForm: form.getFieldValue("planTypeTo"),
      });
      message.error("From plan or to plan not found");
      return;
    }

    if (
      toPlanObj &&
      ["enterprise basic", "freedom basic"].includes(
        toPlanObj.name?.toLowerCase()?.trim()
      )
    ) {
      form.setFieldsValue({
        validity: "Lifetime",
        subscriptionFee: null,
      });
      setIsLifeTimeValidity(true);
    } else {
      form.setFieldsValue({
        validity: null,
        ...(isEdit ? {} : { oneTimeFee: null }),
      });
      setIsLifeTimeValidity(false);
    }

    // Reset the picker option pool so stale configs from a previous
    // from/to selection don't survive across a new fetch.
    setMangoConfigOptions([]);
    setPickedConfigId(null);
    setCalculationLoading(true);
    try {
      const query = new URLSearchParams({
        fromPlatformSetup: fromPlanObj.platform,
        fromTier: fromPlanObj.tier,
        toPlatformSetup: toPlanObj.platform,
        toTier: toPlanObj.tier,
      }).toString();
      const response = await calculatePricingAndLink(query);
      const configs = response?.result?.mangoConfigs;
      if (response?.code === 200 && Array.isArray(configs) && configs.length) {
        setMangoConfigOptions(configs);
        if (configs.length === 1) {
          applyMangoConfig(configs[0]);
        } else {
          setPickedConfigId(configs[0]._id);
          setShowConfigPicker(true);
        }
      } else {
        setIsCreateMangoDeleted(false);
        setCalculatedBasePrice(0);
        setCalculatedMangoLink(null);
        setSaleMangoConfigId(null);
      }
    } catch (error) {
      console.error("Calculation error:", error);
      message.error("Failed to calculate pricing");
    } finally {
      setCalculationLoading(false);
    }
  };

  const applyMangoConfig = (config) => {
    const mango = config?.mango;
    if (!mango) {
      setIsCreateMangoDeleted(false);
      setCalculatedBasePrice(0);
      setCalculatedMangoLink(null);
      setSaleMangoConfigId(null);
      return;
    }
    if (mango.isDeleted && !selectedItem) {
      setIsCreateMangoDeleted(true);
      setShowNoMangoModal(true);
      setCalculatedBasePrice(null);
      setCalculatedMangoLink(null);
      setSaleMangoConfigId(null);
      return;
    }
    setIsCreateMangoDeleted(false);
    setCalculatedBasePrice(mango.inrAmount);
    setCalculatedMangoLink(
      `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${mango._id}`
    );
    setSaleMangoConfigId(config._id);

    // Prefill one-time fee with the discounted price on new items:
    //   - Plan-change basic-tier destinations (matches previous behaviour)
    //   - Any feature item (one-time fee == discounted base price)
    if (!selectedItem) {
      let shouldAutofill = false;
      if (isFeatureItem) {
        shouldAutofill = true;
      } else {
        const toPlanKey = form.getFieldValue("planTypeTo");
        const toPlanObjNow = globalPlans.find((p) => p.key === toPlanKey);
        shouldAutofill = ["enterprise basic", "freedom basic"].includes(
          toPlanObjNow?.name?.toLowerCase()?.trim()
        );
      }
      if (shouldAutofill) {
        const basePrice = Number(mango.inrAmount) || 0;
        const currentDiscount = Number(form.getFieldValue("discount")) || 0;
        const discountedPrice = basePrice - (basePrice * currentDiscount) / 100;
        form.setFieldsValue({
          oneTimeFee: Number(discountedPrice.toFixed(2)),
        });
      }
    }
  };

  const handleConfirmConfigPicker = () => {
    const config = mangoConfigOptions.find((c) => c._id === pickedConfigId);
    if (config) {
      applyMangoConfig(config);
    }
    setShowConfigPicker(false);
    // Keep mangoConfigOptions around so "Change configuration" stays available.
  };

  const handleCancelConfigPicker = () => {
    setShowConfigPicker(false);
    // If nothing has been applied yet, clear calc state so the panel shows
    // "Not Configured" — but keep mangoConfigOptions so the user can reopen.
    if (!saleMangoConfigId) {
      setCalculatedBasePrice(null);
      setCalculatedMangoLink(null);
    }
  };

  const handleReopenConfigPicker = () => {
    if (!mangoConfigOptions.length) return;
    setPickedConfigId(saleMangoConfigId || mangoConfigOptions[0]._id);
    setShowConfigPicker(true);
  };

  const handleFeatureCalculation = async (feature) => {
    if (!feature) {
      setCalculatedBasePrice(null);
      setCalculatedMangoLink(null);
      setSaleMangoConfigId(null);
      setMangoConfigOptions([]);
      setPickedConfigId(null);
      return;
    }
    // Reset the picker option pool so stale configs from a previous
    // feature selection don't survive across a new fetch.
    setMangoConfigOptions([]);
    setPickedConfigId(null);
    setCalculationLoading(true);
    try {
      const response = await fetchFeatureMangoConfig([feature]);
      const configs = response?.result?.mangoConfigs;
      if (response?.code === 200 && Array.isArray(configs) && configs.length) {
        setMangoConfigOptions(configs);
        if (configs.length === 1) {
          applyMangoConfig(configs[0]);
        } else {
          setPickedConfigId(configs[0]._id);
          setShowConfigPicker(true);
        }
      } else {
        setIsCreateMangoDeleted(false);
        setCalculatedBasePrice(0);
        setCalculatedMangoLink(null);
        setSaleMangoConfigId(null);
      }
    } catch (error) {
      console.error("Feature calculation error:", error);
      message.error("Failed to fetch feature pricing");
    } finally {
      setCalculationLoading(false);
    }
  };

  useEffect(() => {
    console.log({ isLifeTimeValidity });
  }, [isLifeTimeValidity]);

  const [itemsData, setItemsData] = useState([]);

  const getPlanDisplayName = (
    platformSetup,
    tier,
    isFeatureItemCtx = false
  ) => {
    if (isFeatureItemCtx) {
      if (!platformSetup && !tier) return "All";
      if (platformSetup === "freedom" && !tier) return "Freedom (All)";
      if (platformSetup === "enterprise" && !tier) return "Enterprise (All)";
    }
    if (!platformSetup) return "-";
    if (platformSetup === "tagmango") return "Free";
    const tierName = tier ? globalMapTierAndPlan(tier) : "";
    const platformName =
      platformSetup.charAt(0).toUpperCase() + platformSetup.slice(1);
    return tierName ? `${platformName} ${tierName}` : platformName;
  };

  const getStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "ACTIVE";
      case "inactive":
        return "INACTIVE";
      case "expired":
        return "EXPIRED";
      case "upcoming":
        return "UPCOMING";
      default:
        return status?.toUpperCase() || "ACTIVE";
    }
  };

  const getPlanKeyFromConfig = (platformSetup, tier) => {
    const plan = [...globalPlans, ...FEATURE_FROM_PLAN_EXTRAS].find(
      (p) => p.platform === platformSetup && p.tier === tier
    );
    return plan?.key || null;
  };

  const mapSaleItemToTableRow = (item, index) => {
    const basePrice = item.mango?.inrAmount || 0;
    const discount = item.discountInPercentage || 0;
    const finalPrice = basePrice - (basePrice * discount) / 100;
    const validityInDays = item.validityInDays || 0;

    const isFeatureSaleItem =
      item.subcategory === "feature" ||
      (Array.isArray(item.features) && item.features.length > 0);

    const fromPlanName = getPlanDisplayName(
      item.fromPlatformSetup,
      item.fromTier,
      isFeatureSaleItem
    );
    const toPlanName = isFeatureSaleItem
      ? item.features?.join(", ") || "-"
      : getPlanDisplayName(item.toPlatformSetup, item.toTier);

    return {
      key: item._id,
      position: index + 1,
      itemName: item.title,
      fromTo: `${fromPlanName} → ${toPlanName}`,
      isFeatureSaleItem,
      features: item.features || [],
      userTypeFrom: getPlanKeyFromConfig(item.fromPlatformSetup, item.fromTier),
      planTypeTo: getPlanKeyFromConfig(item.toPlatformSetup, item.toTier),
      basePrice: basePrice?.toString(),
      discount: discount?.toString(),
      finalPrice: finalPrice?.toFixed(2),
      referEarn: item.isRefernEarnEnabled ? "Yes" : "No",
      noCostEmi: item.noCostEmiEnabled ? "Yes" : "No",
      revShare: (item.refernEarnRevenueShare || 0).toString(),
      coupon: item.couponCode || item.coupon?.code || "-",
      status: getStatusDisplay(item.status),
      link: item.mango?._id
        ? `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${item.mango._id}${
            item.couponCode || item.coupon?.code
              ? `?couponcode=${item.couponCode || item.coupon?.code}`
              : ""
          }`
        : "",
      isExclusive: item.isExclusive || false,
      validityInDays,
      oneTimeFee: (item.onetimeFee || 0).toString(),
      subscriptionFee: (item.subscriptionFee || 0).toString(),
      rawData: item,
    };
  };

  const loadSaleItems = async () => {
    if (!saleId) return;

    setLoader(true);
    try {
      const response = await fetchSaleItems(saleId);
      if (response?.code === 200 && response?.result) {
        const mappedItems = response.result.map(mapSaleItemToTableRow);
        setItemsData(mappedItems);
      } else {
        message.error(response?.message || "Failed to fetch sale items");
      }
    } catch (error) {
      console.error("Error fetching sale items:", error);
      message.error("Failed to fetch sale items");
    } finally {
      setLoader(false);
    }
  };

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Callback function for reorder - will be used to call API
  // Callback function for reorder - calls API to persist the order
  const onReorder = async (updatedRows) => {
    console.log("Reordered items:", updatedRows);

    setReorderLoading(true);
    try {
      // Extract sale item IDs in the new order
      const saleItemIds = updatedRows.map((item) => item.key);

      const response = await reorderSaleItems(saleId, saleItemIds);
      if (response?.success) {
        message.success("Order updated successfully!");
      } else {
        throw new Error(response?.message || "Failed to update order");
      }
    } catch (error) {
      message.error(error?.message || "Failed to update order");
      console.error("Reorder error:", error);
      // Reload items to restore original order on failure
      loadSaleItems();
    } finally {
      setReorderLoading(false);
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setItemsData((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        // Recalculate positions
        const updatedItems = reorderedItems.map((item, index) => ({
          ...item,
          position: index + 1,
        }));

        // Call the onReorder callback with updated items
        onReorder(updatedItems);

        return updatedItems;
      });
    }
  };

  // Fetch sale items when saleId changes
  useEffect(() => {
    loadSaleItems();
  }, [saleId]);

  // Sample sale data
  useEffect(() => {
    const saleDataParsed =
      !!searchParams && searchParams.get("record")
        ? JSON.parse(searchParams.get("record"))?.rawData
        : null;
    setSalesData({
      id: saleDataParsed?._id,
      salesName: saleDataParsed?.title,
      description: saleDataParsed?.description,
      validFrom: saleDataParsed?.validFrom,
      validTo: saleDataParsed?.validTill,
      createdBy: saleDataParsed?.createdBy?.name,
      status: saleDataParsed?.status,
    });
  }, []);

  const handleAddItem = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
    const values = form.getFieldsValue();
    console.log(values);
    form.resetFields();
    form.setFieldsValue({
      overridePaymentGateway: false,
      defaultPaymentGateway: "indian",
      noCostEmiEnabled: false,
      showInRefernEarnDashboard: false,
    });
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);

    // Set fromPlan and toPlan states for the Select components
    setFromPlan(item.userTypeFrom);
    setToPlan(item.planTypeTo);

    // Convert string values to numbers for form fields that expect numbers
    const rawDiscount =
      item.rawData?.discountInPercentage ?? item.discount ?? 0;
    const discountValue = Number(rawDiscount) || 0;
    const validityInDaysValue =
      item.validityInDays || item.rawData?.validityInDays || 0;
    const oneTimeFeeValue = item.oneTimeFee
      ? Number(Number(item.oneTimeFee)?.toFixed(2))
      : 0;
    const subscriptionFeeValue = item.subscriptionFee
      ? Number(Number(item.subscriptionFee)?.toFixed(2))
      : 0;
    const revShareValue = item.revShare
      ? Number(Number(item.revShare)?.toFixed(2))
      : 0;
    setCalculatedBasePrice(Number(item.basePrice) || 0);
    setCalculatedMangoLink(item.link);

    const rawDefaultPaymentGateway = item.rawData?.defaultPaymentGateway;
    const overridePaymentGatewayValue =
      rawDefaultPaymentGateway === "indian" ||
      rawDefaultPaymentGateway === "international";
    const defaultPaymentGatewayValue = overridePaymentGatewayValue
      ? rawDefaultPaymentGateway
      : "indian";

    const noCostEmiEnabledValue = !!item.rawData?.noCostEmiEnabled;

    const refernEarnProductStatusValue =
      item.rawData?.refernEarnProductStatus === "listed" ? "listed" : "hidden";
    const showInRefernEarnDashboardValue =
      refernEarnProductStatusValue === "listed";

    // Store original values for comparison during update
    const origValues = {
      title: item.itemName,
      isExclusive: item.isExclusive || false,
      discountInPercentage: discountValue,
      validityInDays: validityInDaysValue,
      onetimeFee: Number(oneTimeFeeValue),
      subscriptionFee: Number(subscriptionFeeValue),
      isRefernEarnEnabled: item.rawData?.isRefernEarnEnabled || false,
      refernEarnRevenueShare: revShareValue,
      refernEarnProductStatus: refernEarnProductStatusValue,
      status: item.rawData?.status || item.status?.toLowerCase(),
      defaultPaymentGateway: overridePaymentGatewayValue
        ? defaultPaymentGatewayValue
        : null,
      noCostEmiEnabled: noCostEmiEnabledValue,
    };

    form.setFieldsValue({
      itemName: item.itemName,
      isExclusive: item.isExclusive,
      userTypeFrom: item.userTypeFrom,
      planTypeTo: item.planTypeTo,
      discount: discountValue,
      validity: validityInDaysValue,
      oneTimeFee: oneTimeFeeValue,
      subscriptionFee: subscriptionFeeValue,
      applicableForRefer: item.rawData?.isRefernEarnEnabled,
      revShare: revShareValue,
      showInRefernEarnDashboard: showInRefernEarnDashboardValue,
      status: item.status,
      overridePaymentGateway: overridePaymentGatewayValue,
      defaultPaymentGateway: defaultPaymentGatewayValue,
      noCostEmiEnabled: noCostEmiEnabledValue,
    });

    const isFeatureSaleItem =
      item.isFeatureSaleItem ||
      item.rawData?.subcategory === "feature" ||
      (Array.isArray(item.rawData?.features) &&
        item.rawData.features.length > 0);

    if (isFeatureSaleItem) {
      const firstFeature = item.rawData?.features?.[0] || null;
      setIsFeatureItem(true);
      setSelectedFeature(firstFeature);
      setSaleMangoConfigId(item.rawData?.saleMangoConfig?._id || null);

      // Base validity/subscription rendering on the item's own data — some
      // feature items were created before the "lifetime + no subscription" rule.
      const isLifetimeForFeature = validityInDaysValue === 0;
      setIsLifeTimeValidity(isLifetimeForFeature);
      form.setFieldsValue({
        isFeatureItem: true,
        feature: firstFeature,
        validity: isLifetimeForFeature ? "Lifetime" : validityInDaysValue,
      });
      if (isLifetimeForFeature) {
        origValues.validityInDays = 0;
      }
    } else {
      setIsFeatureItem(false);
      setSelectedFeature(null);

      // Trigger calculation for edit mode
      if (item.userTypeFrom && item.planTypeTo) {
        handleCalculation(item.userTypeFrom, item.planTypeTo, { isEdit: true });
      }
      const toPlanName = item.fromTo.split(" → ")[1];
      if (
        ["enterprise basic", "freedom basic"].includes(
          toPlanName?.trim()?.toLowerCase()
        )
      ) {
        setIsLifeTimeValidity(true);
        form.setFieldsValue({
          validity: "Lifetime",
        });
        origValues.validityInDays = 0;
      } else {
        setIsLifeTimeValidity(false);
        form.setFieldsValue({
          validity: validityInDaysValue,
        });
      }
    }
    setOriginalValues(origValues);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setOriginalValues(null);
    setCalculatedBasePrice(null);
    setCalculatedMangoLink(null);
    setFromPlan(null);
    setToPlan(null);
    setIsLifeTimeValidity(false);
    setIsCreateMangoDeleted(false);
    setIsFeatureItem(false);
    setSelectedFeature(null);
    setSaleMangoConfigId(null);
    setMangoConfigOptions([]);
    setShowConfigPicker(false);
    setPickedConfigId(null);
    form.resetFields();
  };

  const handleFormSubmit = async (values) => {
    console.log("Form values:", values);
    console.log("Selected item:", selectedItem);

    if (!selectedItem && isCreateMangoDeleted) {
      setShowNoMangoModal(true);
      return;
    }

    const allValues = form.getFieldsValue();
    if (!allValues.itemName?.trim()) {
      message.error("Item name is required");
      return;
    }

    if (!allValues?.discount) {
      message.error("Discount Value must be 1% or higher.");
      return;
    }

    // Calculate discounted price
    const discountedPrice =
      calculatedBasePrice - (calculatedBasePrice * allValues.discount) / 100;

    const subscriptionFeeValue = isLifeTimeValidity
      ? 0
      : allValues.subscriptionFee || 0;
    const summationValue =
      Number(allValues.oneTimeFee || 0) + Number(subscriptionFeeValue);
    if (Math.round(summationValue) !== Math.round(discountedPrice)) {
      const errorMsg = isLifeTimeValidity
        ? `One-time Fee should equal ₹${discountedPrice?.toFixed(2)}`
        : `One-time Fee + Subscription Fee should equal ₹${discountedPrice.toFixed(
            2
          )}`;
      message.error(errorMsg);
      return;
    }

    // Get from and to plan objects (feature items may pick a null-tier extra)
    const fromPlanObj = [...globalPlans, ...FEATURE_FROM_PLAN_EXTRAS].find(
      (p) => p.key === fromPlan
    );
    const toPlanObj = globalPlans.find((p) => p.key === toPlan);

    if (isFeatureItem) {
      if (!fromPlanObj) {
        message.error("Please select User Type");
        return;
      }
      if (!selectedFeature) {
        message.error("Please select a Feature");
        return;
      }
    } else if (!fromPlanObj || !toPlanObj) {
      message.error("Please select both User Type and Plan Type");
      return;
    }

    // Get validity in days (0 for lifetime)
    const validityInDays = isLifeTimeValidity
      ? 0
      : parseInt(values.validity, 10) || 1;

    setSubmitLoading(true);
    try {
      if (selectedItem) {
        // Build current values object for comparison
        const currentValues = {
          title: values.itemName.trim(),
          isExclusive: values.isExclusive || false,
          discountInPercentage: values.discount || 0,
          onetimeFee: values.oneTimeFee || 0,
          subscriptionFee: values.subscriptionFee || 0,
          isRefernEarnEnabled: values.applicableForRefer || false,
          refernEarnRevenueShare: values.revShare || 0,
          refernEarnProductStatus: values.showInRefernEarnDashboard
            ? "listed"
            : "hidden",
          defaultPaymentGateway: values.overridePaymentGateway
            ? values.defaultPaymentGateway
            : null,
          noCostEmiEnabled: !!values.noCostEmiEnabled,
        };
        // Feature items don't have a validity concept — skip the field entirely.
        if (!isFeatureItem) {
          currentValues.validityInDays = validityInDays;
        }

        // Compare and only include modified fields
        const modifiedPayload = {};
        Object.keys(currentValues).forEach((key) => {
          if (originalValues && currentValues[key] !== originalValues[key]) {
            modifiedPayload[key] = currentValues[key];
          }
        });

        // If no changes were made, show message and return
        if (Object.keys(modifiedPayload).length === 0) {
          message.info("No changes detected");
          setSubmitLoading(false);
          handleModalCancel();
          return;
        }

        console.log("Modified payload:", modifiedPayload);

        // Call update API
        const response = await updateSaleItem(
          saleId,
          selectedItem.key,
          modifiedPayload
        );
        if (response?.success) {
          message.success("Item updated successfully!");
          loadSaleItems();
          handleModalCancel();
        } else {
          throw new Error(response?.message || "Failed to update item");
        }
      } else {
        if (!saleMangoConfigId) {
          message.error(
            "Mango configuration missing. Please re-select the plan/feature."
          );
          setSubmitLoading(false);
          return;
        }
        // Build API payload for create
        const payload = {
          title: values.itemName.trim(),
          isExclusive: values.isExclusive || false,
          fromPlatformSetup: fromPlanObj.platform,
          fromTier: fromPlanObj.tier,
          saleMangoConfig: saleMangoConfigId,
          discountInPercentage: values.discount || 0,
          onetimeFee: values.oneTimeFee || 0,
          subscriptionFee: values.subscriptionFee || 0,
          isRefernEarnEnabled: values.applicableForRefer || false,
          refernEarnRevenueShare: values.revShare || 0,
          refernEarnProductStatus: values.showInRefernEarnDashboard
            ? "listed"
            : "hidden",
          defaultPaymentGateway: values.overridePaymentGateway
            ? values.defaultPaymentGateway
            : null,
          noCostEmiEnabled: !!values.noCostEmiEnabled,
        };

        if (isFeatureItem) {
          payload.subcategory = "feature";
          payload.features = [selectedFeature];
        } else {
          payload.toPlatformSetup = toPlanObj.platform;
          payload.toTier = toPlanObj.tier;
          payload.validityInDays = validityInDays;
        }

        // Create new item
        const response = await createSaleItem(saleId, payload);
        if (response?.success) {
          message.success("Item added successfully!");
          loadSaleItems();
        } else {
          throw new Error(response?.message || "Failed to create item");
        }
      }
      handleModalCancel();
    } catch (error) {
      console.error("Submit error:", error);
      message.error(error?.message || "Failed to save item. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleHideOption = (status) => {
    const saleStatus = saleData?.status?.toLowerCase()?.trim();
    const result = {
      markAsInactive: false,
      markAsExpired: false,
      markAsActive: false,
    };
    const localStatus = status?.toLowerCase()?.trim();

    switch (saleStatus) {
      case "upcoming":
        if (localStatus === "upcoming") {
          result.markAsInactive = true;
          result.markAsExpired = false;
          result.markAsActive = true;
        } else if (localStatus === "expired") {
          result.markAsInactive = true;
          result.markAsExpired = true;
          result.markAsActive = true;
        } else if (localStatus === "active") {
          result.markAsInactive = false;
          result.markAsExpired = false;
          result.markAsActive = true;
        } else {
          result.markAsInactive = false;
          result.markAsExpired = false;
          result.markAsActive = true;
        }
        break;

      case "active":
        if (localStatus === "active") {
          result.markAsInactive = false;
          result.markAsExpired = false;
          result.markAsActive = true;
        } else if (localStatus === "inactive") {
          result.markAsInactive = true;
          result.markAsExpired = false;
          result.markAsActive = false;
        } else if (localStatus === "expired") {
          result.markAsInactive = false;
          result.markAsExpired = true;
          result.markAsActive = false;
        } else {
          result.markAsInactive = false;
          result.markAsExpired = false;
          result.markAsActive = false;
        }
        break;

      case "expired":
        if (localStatus === "expired") {
          result.markAsInactive = false;
          result.markAsExpired = true;
          result.markAsActive = true;
        } else if (localStatus === "inactive") {
          result.markAsInactive = true;
          result.markAsExpired = false;
          result.markAsActive = true;
        } else {
          result.markAsInactive = false;
          result.markAsExpired = false;
          result.markAsActive = false;
        }
        break;
      default:
        result.markAsInactive = false;
        result.markAsExpired = false;
        result.markAsActive = false;
    }
    return result;
  };

  const handleStatusChange = (record, newStatus) => {
    const statusLabels = {
      active: "Active",
      inactive: "Inactive",
      expired: "Expired",
    };

    Modal.confirm({
      title: `Mark as ${statusLabels[newStatus]} ?`,
      content: (
        <Typography.Text>
          Are you sure you want to {statusLabels[newStatus]} this
          <strong>&nbsp;&quot;{record.itemName}&quot;</strong> as{" "}
          <span>
            {/* eslint-disable-next-line no-nested-ternary */}
            {statusLabels[newStatus] === "Expired"
              ? "Once expired, the link will no longer be accessible to any user."
              : statusLabels[newStatus] === "Inactive"
              ? "Users who already have the link will still be able to access it."
              : "Once activated, the item will become available to users based on its configured validity period."}
          </span>
        </Typography.Text>
      ),
      okText: "Yes, Confirm",
      cancelText: "Cancel",
      okButtonProps: {
        style: { background: "#7c3aed", borderColor: "#7c3aed" },
      },
      centered: true,
      onOk: async () => {
        try {
          const response = await updateSaleItem(saleId, record.key, {
            status: newStatus,
          });
          if (response?.success) {
            message.success(`Item marked as ${statusLabels[newStatus]}`);
            loadSaleItems();
          } else {
            throw new Error(
              response?.message ||
                `Failed to mark item as ${statusLabels[newStatus]}`
            );
          }
        } catch (error) {
          console.error("Status change error:", error);
          message.error(
            error?.message ||
              `Failed to mark item as ${statusLabels[newStatus]}`
          );
        }
      },
    });
  };

  const handleMenuAction = (action, record) => {
    switch (action) {
      case "copy":
        navigator.clipboard.writeText(record.link);
        message.success("Link copied to clipboard!");
        break;
      case "edit":
        handleEditItem(record);
        break;
      case "inactive":
        handleStatusChange(record, "inactive");
        break;
      case "active":
        handleStatusChange(record, "active");
        break;
      case "expired":
        handleStatusChange(record, "expired");
        break;
      case "delete":
        console.log("Delete item:", record);
        message.error("Item deleted");
        break;
      default:
        break;
    }
  };

  const getItemActionMenu = (record) => {
    const hideObj = handleHideOption(record.status);
    return [
      {
        key: "copy",
        label: "Copy Link",
        icon: <CopyOutlined style={{ color: "#7c3aed" }} />,
        hide: record.status === "EXPIRED",
        onClick: () => handleMenuAction("copy", record),
      },
      {
        key: "edit",
        label: "Edit Item",
        icon: <EditOutlined style={{ color: "#7c3aed" }} />,
        hide: false,
        onClick: () => handleMenuAction("edit", record),
      },
      {
        key: "inactive",
        label: "Mark as Inactive",
        icon: <CloseOutlined />,
        hide: hideObj.markAsInactive,
        onClick: () => handleMenuAction("inactive", record),
      },
      {
        key: "active",
        label: "Mark as Active",
        icon: <CheckOutlined />,
        hide: hideObj.markAsActive,
        onClick: () => handleMenuAction("active", record),
      },
      {
        key: "expired",
        label: "Mark as Expired",
        icon: <CloseOutlined style={{ color: "#ef4444" }} />,
        hide: hideObj.markAsExpired,
        onClick: () => handleMenuAction("expired", record),
      },
      // {
      //   key: "delete",
      //   label: "Delete Item",
      //   icon: <DeleteOutlined />,
      //   danger: true,
      //   onClick: () => handleMenuAction("delete", record),
      // },
    ].filter((item) => !item.hide);
  };

  const displayItemsTable = () => {
    const columns = [
      {
        title: "",
        dataIndex: "drag",
        key: "drag-handle",
        width: 60,
        fixed: "left",
        render: () => null,
      },
      {
        title: (
          <>
            <Space>
              <span>ITEM NAME</span>
              <Tooltip title="This will display as product name to the creator.">
                <InfoCircleOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            </Space>
          </>
        ),
        dataIndex: "itemName",
        key: "itemName",
        width: 200,
        render: (text, record) => (
          <Space>
            <Typography.Text style={{ fontWeight: 500 }}>
              {text}
            </Typography.Text>
            {record.isExclusive && (
              <Tooltip title="Exclusive">
                <StarFilled style={{ color: "#faad14", fontSize: 14 }} />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "LINK",
        dataIndex: "link",
        key: "link",
        width: 180,
        render: (link, record) => {
          const mango = record.rawData?.mango;
          const isMangoDeleted = mango?.isDeleted;
          const isMangoHidden = mango?.isHidden;
          const isDisabled = record.status === "EXPIRED" || isMangoDeleted;
          return (
            <div>
              <p
                style={{
                  color: "#7c3aed",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  margin: 0,
                  opacity: isDisabled ? 0.5 : 1,
                }}
                onClick={async () => {
                  if (isDisabled) return;
                  await navigator.clipboard.writeText(link);
                  message.success("Link copied to clipboard!");
                }}
              >
                Copy&nbsp;Link
              </p>
              {(isMangoDeleted || isMangoHidden) && (
                <Typography.Text
                  style={{
                    display: "block",
                    color: "#ef4444",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  {isMangoDeleted
                    ? "This Mango has been deleted."
                    : "This Mango is currently hidden in your account."}
                </Typography.Text>
              )}
            </div>
          );
        },
      },
      {
        title: "STATUS",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status) => {
          let color = "default";
          if (status === "ACTIVE") {
            color = "success";
          } else if (status === "INACTIVE") {
            color = "default";
          } else if (status === "EXPIRED") {
            color = "error";
          } else if (status === "UPCOMING") {
            color = "processing";
          }
          return (
            <Tag color={color} bordered={false}>
              {status}
            </Tag>
          );
        },
      },

      {
        title: "FROM → TO",
        dataIndex: "fromTo",
        key: "fromTo",
        width: 200,
        render: (_, record) => {
          return (
            <Typography.Text style={{ fontWeight: 500 }}>
              <p style={{ margin: 0 }}>{record.fromTo}</p>
              {record.validityInDays ? (
                <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                  validity : {record.validityInDays} days
                </p>
              ) : null}
            </Typography.Text>
          );
        },
      },
      {
        title: "FEATURES",
        dataIndex: "features",
        key: "features",
        width: 140,
        render: (_, record) => (
          <Typography.Text>
            {record.features?.length ? record.features.join(", ") : "-"}
          </Typography.Text>
        ),
      },
      {
        title: "BASE PRICE",
        dataIndex: "basePrice",
        key: "basePrice",
        width: 120,
        render: (text) => <span>₹{Number(text)?.toFixed(2)}</span>,
      },
      {
        title: "DISCOUNT",
        dataIndex: "discount",
        key: "discount",
        width: 110,
        render: (text) => <span>{text}%</span>,
      },
      {
        title: "FINAL PRICE",
        dataIndex: "finalPrice",
        key: "finalPrice",
        width: 130,
        render: (text) => (
          <Typography.Text style={{ fontWeight: 500 }}>₹{text}</Typography.Text>
        ),
      },
      {
        title: "COUPON",
        dataIndex: "coupon",
        key: "coupon",
        width: 140,
      },
      {
        title: (
          <>
            <Space>
              <span>REFER&nbsp;&&nbsp;EARN</span>
              <Tooltip title="This item will be visible under Refer & Earn of the creator's dashboard.">
                <InfoCircleOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            </Space>
          </>
        ),
        dataIndex: "referEarn",
        key: "referEarn",
        width: 140,
        render: (text) => <span>{text}</span>,
      },
      {
        title: "NO COST EMI",
        dataIndex: "noCostEmi",
        key: "noCostEmi",
        width: 140,
        render: (text) => <span>{text}</span>,
      },
      {
        title: "REV. SHARE",
        dataIndex: "revShare",
        key: "revShare",
        width: 120,
        render: (text) => <span>{text}%</span>,
      },

      {
        title: "ACTION",
        dataIndex: "action",
        key: "action",
        width: 90,
        fixed: "right",
        render: (text, record) => {
          const isMangoDeleted = record.rawData?.mango?.isDeleted;
          return (
            <Dropdown
              menu={{ items: getItemActionMenu(record) }}
              trigger={["click"]}
              placement="bottomRight"
              disabled={isMangoDeleted}
            >
              <Button
                type="text"
                icon={<MoreOutlined style={{ fontSize: 20 }} />}
                disabled={isMangoDeleted}
                onClick={(e) => e.preventDefault()}
              />
            </Dropdown>
          );
        },
      },
    ];

    // Filter data based on search query and status tab (frontend filtering)
    const filteredData = itemsData.filter((item) => {
      // Status filter
      const statusMatch =
        activeTab === "all" ||
        item.status.toLowerCase() === activeTab.toLowerCase();

      // Search filter - search across multiple fields
      const searchLower = searchQuery.toLowerCase().trim();
      const searchMatch =
        !searchLower ||
        item.itemName?.toLowerCase().includes(searchLower) ||
        item.fromTo?.toLowerCase().includes(searchLower) ||
        item.coupon?.toLowerCase().includes(searchLower) ||
        item.basePrice?.toString().includes(searchLower) ||
        item.finalPrice?.toString().includes(searchLower);

      return statusMatch && searchMatch;
    });

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={filteredData.map((item) => item.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loader || reorderLoading}
            pagination={false}
            locale={{
              emptyText: "No items added to this sale yet.",
            }}
            scroll={{ x: 1500 }}
            components={{
              body: {
                row: DraggableRow,
              },
            }}
            rowKey="key"
          />
        </SortableContext>
      </DndContext>
    );
  };

  const [tabItems, setTabItems] = useState([
    {
      key: "all",
      label: "All",
      hide: false,
    },
    {
      key: "upcoming",
      label: "Upcoming",
      hide: false,
    },
    {
      key: "active",
      label: "Active",
      hide: false,
    },
    {
      key: "inactive",
      label: "Inactive",
      hide: false,
    },
    {
      key: "expired",
      label: "Expired",
      hide: false,
    },
  ]);

  useEffect(() => {
    switch (saleData?.status) {
      case "upcoming":
        setTabItems([
          {
            key: "all",
            label: "All",
            hide: false,
          },
          {
            key: "upcoming",
            label: "Upcoming",
            hide: false,
          },
          {
            key: "expired",
            label: "Expired",
            hide: false,
          },
        ]);
        break;
      case "active":
        setTabItems([
          {
            key: "all",
            label: "All",
            hide: false,
          },

          {
            key: "active",
            label: "Active",
            hide: false,
          },
          {
            key: "inactive",
            label: "Inactive",
            hide: false,
          },
          {
            key: "expired",
            label: "Expired",
            hide: false,
          },
        ]);
        break;
      case "expired":
        setTabItems([
          {
            key: "all",
            label: "All",
            hide: false,
          },
          {
            key: "inactive",
            label: "Inactive",
            hide: false,
          },
          {
            key: "expired",
            label: "Expired",
            hide: false,
          },
        ]);
        break;
      default:
        break;
    }
  }, [saleData]);

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <Link
          to={ROUTE_PATHS.SALES_DASHBOARD}
          style={{
            display: "inline-flex",
            alignItems: "center",
            color: "#6b7280",
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <LeftOutlined style={{ marginRight: 8 }} />
          Back to Sales
        </Link>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
              {saleData?.salesName?.length > 80
                ? `${saleData?.salesName?.slice(0, 80)}...`
                : saleData?.salesName}
            </Title>
            {/* <Text type="secondary" style={{ fontSize: 14 }}>
              {saleData.description}
            </Text> */}
          </div>

          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAddItem}
            style={{
              background: "#7c3aed",
              borderColor: "#7c3aed",
            }}
          >
            Add Item
          </Button>
        </div>

        {/* <Space size="middle" style={{ color: "#6b7280", fontSize: 14 }}>
          <Space size="small">
            <CalendarOutlined />
            <Text type="secondary">
              {saleData.validFrom} - {saleData.validTo}
            </Text>
          </Space>
          <Space size="small">
            <UserOutlined />
            <Text type="secondary">Created by {saleData.createdBy}</Text>
          </Space>
        </Space> */}
      </div>

      <div
        style={{
          background: "#fff",
          padding: "14px",
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <FiltersWrapper
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <CustomSearchbar
            placeholder="Search by item name and coupon"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            style={{ maxWidth: "70%" }}
          />

          <Select
            value={activeTab}
            onChange={setActiveTab}
            size="large"
            style={{ width: 200 }}
            disabled={itemsData.length === 0}
          >
            {tabItems.map((item) => (
              <Select.Option key={item.key} value={item.key}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </FiltersWrapper>

        {displayItemsTable()}
      </div>

      {/* Add/Edit Item Modal */}
      <Modal
        title={selectedItem ? "Edit Item" : "Add New Item"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
        destroyOnClose
        centered
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingRight: 8,
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onValuesChange={(changedValues) => {
            if (!("discount" in changedValues)) return;
            const toPlanKey = form.getFieldValue("planTypeTo");
            const toPlanObj = globalPlans.find((p) => p.key === toPlanKey);
            const isToBasic =
              toPlanObj &&
              ["enterprise basic", "freedom basic"].includes(
                toPlanObj.name?.toLowerCase()?.trim()
              );
            if (!isToBasic) return;
            const basePrice = Number(calculatedBasePrice) || 0;
            const discount = Number(changedValues.discount) || 0;
            const discountedPrice = basePrice - (basePrice * discount) / 100;
            form.setFieldsValue({
              oneTimeFee: Number(discountedPrice.toFixed(2)),
            });
          }}
          style={{ marginTop: -10 }}
        >
          <Form.Item
            label={
              <Space>
                <span style={{ fontWeight: 600, color: "#6b7280" }}>
                  ITEM NAME
                </span>
                <Tooltip title="This will display as product name to the creator.">
                  <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                </Tooltip>
              </Space>
            }
            style={{ marginBottom: 24 }}
            name="itemName"
            rules={[
              {
                required: true,
                message: "Please select the item name!",
              },
            ]}
          >
            {isFeatureItem ? (
              <Input
                size="large"
                placeholder="Enter item name"
                style={{ borderRadius: 8 }}
              />
            ) : (
              <Select
                size="large"
                placeholder="Select item name"
                style={{ borderRadius: 8 }}
                options={
                  selectedItem?.itemName &&
                  !itemNameOptions.some(
                    (o) => o.value === selectedItem.itemName
                  )
                    ? [
                        ...itemNameOptions,
                        {
                          label: selectedItem.itemName,
                          value: selectedItem.itemName,
                        },
                      ]
                    : itemNameOptions
                }
                showSearch
                optionFilterProp="label"
              />
            )}
          </Form.Item>

          <div
            style={{
              display: "flex",
              marginBottom: 12,
            }}
          >
            <Form.Item
              name="isExclusive"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox />
            </Form.Item>
            <div style={{ marginLeft: 10, position: "relative", top: 4 }}>
              <span>Mark as Exclusive</span>
              <Tooltip title={`"Exclusive" tag will be displayed to users.`}>
                <InfoCircleOutlined
                  style={{ color: "#9ca3af", marginLeft: 10 }}
                />
              </Tooltip>
            </div>
          </div>

          {(!selectedItem || isFeatureItem) && (
            <div style={{ display: "flex", marginBottom: 24 }}>
              <Form.Item
                name="isFeatureItem"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox
                  disabled={!!selectedItem}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setIsFeatureItem(checked);
                    setCalculatedBasePrice(null);
                    setCalculatedMangoLink(null);
                    setIsCreateMangoDeleted(false);
                    if (checked) {
                      form.setFieldsValue({
                        itemName: null,
                        planTypeTo: null,
                        validity: "Lifetime",
                        subscriptionFee: null,
                      });
                      setToPlan(null);
                      setIsLifeTimeValidity(true);
                    } else {
                      setSelectedFeature(null);
                      form.setFieldsValue({
                        itemName: null,
                        feature: null,
                        validity: null,
                        userTypeFrom: null,
                      });
                      setFromPlan(null);
                      setIsLifeTimeValidity(false);
                    }
                  }}
                />
              </Form.Item>
              <div style={{ marginLeft: 10, position: "relative", top: 4 }}>
                <span>Create Feature Item</span>
                <Tooltip title="Create a sale item tied to a platform feature (e.g. AI_VERSE)">
                  <InfoCircleOutlined
                    style={{ color: "#9ca3af", marginLeft: 10 }}
                  />
                </Tooltip>
              </div>
            </div>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    USER TYPE (FROM)
                  </span>
                }
                name="userTypeFrom"
                rules={[
                  { required: true, message: "Please select user type!" },
                ]}
                style={{ marginBottom: 24 }}
              >
                <Select
                  size="large"
                  placeholder="Select Plan"
                  style={{ borderRadius: 8 }}
                  onChange={(value) => {
                    setFromPlan(value);
                    if (isFeatureItem) return;
                    form.setFieldsValue({
                      planTypeTo: null,
                    });
                    setToPlan(null);
                    const planTypeTo = form.getFieldValue("planTypeTo");
                    handleCalculation(value, planTypeTo);
                  }}
                  disabled={!!selectedItem}
                >
                  {(isFeatureItem
                    ? [
                        globalPlans.find((p) => p.key === 1), // Free
                        FEATURE_FROM_PLAN_EXTRAS.find(
                          (p) => p.key === "freedom-all"
                        ),
                        globalPlans.find((p) => p.key === 2), // Freedom Basic
                        globalPlans.find((p) => p.key === 3), // Freedom Pro
                        globalPlans.find((p) => p.key === 4), // Freedom Advanced
                        FEATURE_FROM_PLAN_EXTRAS.find(
                          (p) => p.key === "enterprise-all"
                        ),
                        globalPlans.find((p) => p.key === 5), // Enterprise Basic
                        globalPlans.find((p) => p.key === 6), // Enterprise Pro
                        globalPlans.find((p) => p.key === 7), // Enterprise Advanced
                        FEATURE_FROM_PLAN_EXTRAS.find((p) => p.key === "all"),
                      ].filter(Boolean)
                    : globalPlans.filter((p) => p.key !== 8)
                  ).map((plan) => (
                    <Select.Option key={plan.key} value={plan.key}>
                      {plan.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {isFeatureItem ? (
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      FEATURES
                    </span>
                  }
                  name="feature"
                  rules={[
                    { required: true, message: "Please select a feature!" },
                  ]}
                  style={{ marginBottom: 24 }}
                >
                  <Select
                    size="large"
                    placeholder="Select Feature"
                    style={{ borderRadius: 8 }}
                    onChange={(value) => {
                      setSelectedFeature(value);
                      handleFeatureCalculation(value);
                    }}
                    disabled={!!selectedItem}
                  >
                    {AVAILABLE_FEATURES.map((f) => (
                      <Select.Option key={f.value} value={f.value}>
                        {f.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      PLAN TYPE (TO)
                    </span>
                  }
                  name="planTypeTo"
                  rules={[
                    { required: true, message: "Please select plan type!" },
                  ]}
                  style={{ marginBottom: 24 }}
                >
                  <Select
                    size="large"
                    placeholder="Select Plan"
                    style={{ borderRadius: 8 }}
                    onChange={(value) => {
                      console.log({ fromPlan: value });
                      setToPlan(value);
                      const userTypeFrom = form.getFieldValue("userTypeFrom");
                      handleCalculation(userTypeFrom, value);
                    }}
                    disabled={!!selectedItem}
                  >
                    {fromPlan ? (
                      globalPlans
                        .filter((plan) => {
                          return plan.key > fromPlan;
                        })
                        .map((plan) => (
                          <Select.Option key={plan.key} value={plan.key}>
                            {plan.name}
                          </Select.Option>
                        ))
                    ) : (
                      <Select.Option value={null} disabled>
                        Choose User Type First
                      </Select.Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Calculation Section - Shows when both userTypeFrom and planTypeTo (or feature) are selected */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.userTypeFrom !== currentValues.userTypeFrom ||
              prevValues.planTypeTo !== currentValues.planTypeTo ||
              prevValues.feature !== currentValues.feature
            }
            style={{ marginBottom: 24 }}
          >
            {({ getFieldValue }) => {
              const userTypeFrom = getFieldValue("userTypeFrom");
              const planTypeTo = getFieldValue("planTypeTo");
              const feature = getFieldValue("feature");

              const hasTarget = isFeatureItem ? !!feature : !!planTypeTo;
              if (!userTypeFrom || !hasTarget) return null;

              return (
                <>
                  <div
                    style={{
                      background: "#fff4e8",
                      border: "1px solid #f18926",
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 24,
                    }}
                  >
                    {calculationLoading ? (
                      <div style={{ textAlign: "center", padding: "8px 0" }}>
                        <Typography.Text style={{ color: "#f18926" }}>
                          Calculating...
                        </Typography.Text>
                      </div>
                    ) : (
                      <>
                        <Row
                          justify="space-between"
                          align="middle"
                          style={{
                            marginBottom: 8,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Col>
                            <Typography.Text
                              style={{ color: "#f18926", fontWeight: 500 }}
                            >
                              Mango Link:
                            </Typography.Text>
                          </Col>
                          <Col>
                            {calculatedMangoLink ? (
                              <Space>
                                <Typography.Text
                                  style={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "inline-block",
                                    marginTop: 8,
                                  }}
                                >
                                  {calculatedMangoLink}
                                </Typography.Text>

                                <Button
                                  type="link"
                                  size="small"
                                  icon={<CopyOutlined />}
                                  style={{
                                    color: "#f18926",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      calculatedMangoLink
                                    );
                                    message.success(
                                      "Link copied to clipboard!"
                                    );
                                  }}
                                />
                              </Space>
                            ) : (
                              <Typography.Text style={{ color: "#9ca3af" }}>
                                Not Configured
                              </Typography.Text>
                            )}
                          </Col>
                        </Row>
                        <Row justify="space-between" align="middle">
                          <Col>
                            <Typography.Text
                              style={{ color: "#f18926", fontWeight: 500 }}
                            >
                              Base Price:
                            </Typography.Text>
                          </Col>
                          <Col>
                            <Typography.Text
                              style={{ color: "#f18926", fontWeight: 600 }}
                            >
                              ₹{(Number(calculatedBasePrice) || 0).toFixed(2)}
                            </Typography.Text>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                  {!selectedItem && mangoConfigOptions.length > 1 && (
                    <div style={{ marginTop: -12, marginBottom: 24 }}>
                      <Button
                        type="dashed"
                        icon={<SwapOutlined />}
                        onClick={handleReopenConfigPicker}
                        block
                        style={{
                          borderColor: "#7c3aed",
                          color: "#7c3aed",
                          fontWeight: 500,
                        }}
                      >
                        {saleMangoConfigId
                          ? "Change Mango Configuration"
                          : "Select Mango Configuration"}
                      </Button>
                    </div>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={isFeatureItem ? 24 : 12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    DISCOUNT
                  </span>
                }
                name="discount"
                required
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        value === undefined ||
                        value === null ||
                        value === ""
                      ) {
                        return Promise.reject(
                          new Error("Please enter discount")
                        );
                      }
                      if (value > 100) {
                        return Promise.reject(
                          new Error("Discount cannot be greater than 100%")
                        );
                      }
                      if (value < 0) {
                        return Promise.reject(
                          new Error("Discount cannot be negative")
                        );
                      }
                      const decimals = value.toString().split(".")[1];
                      if (decimals && decimals.length > 3) {
                        return Promise.reject(
                          new Error("Up to 3 decimal places allowed")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: 24 }}
              >
                <InputNumber
                  size="large"
                  placeholder="0"
                  min={0}
                  max={100}
                  step={0.001}
                  precision={3}
                  style={{ width: "100%", borderRadius: 8 }}
                  addonAfter="%"
                  onChange={(value) => {
                    if (!isFeatureItem) return;
                    const basePrice = Number(calculatedBasePrice) || 0;
                    const discount = Number(value) || 0;
                    const discountedPrice =
                      basePrice - (basePrice * discount) / 100;
                    form.setFieldsValue({
                      oneTimeFee: Number(discountedPrice.toFixed(2)),
                    });
                  }}
                />
              </Form.Item>
            </Col>

            {!isFeatureItem && (
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      VALIDITY <small>(in days)</small>
                    </span>
                  }
                  name="validity"
                  rules={[
                    {
                      required: !isLifeTimeValidity,
                      message: "Please enter validity in days!",
                    },
                  ]}
                  style={{ marginBottom: 24 }}
                >
                  {isLifeTimeValidity ? (
                    <Input
                      size="large"
                      placeholder="Lifetime"
                      value="Lifetime"
                      disabled
                      style={{ width: "100%", borderRadius: 8, padding: 8 }}
                    />
                  ) : (
                    <InputNumber
                      size="large"
                      placeholder="Enter days"
                      min={1}
                      style={{ width: "100%", borderRadius: 8 }}
                    />
                  )}
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Discounted Price Section - Shows when discount is entered */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.discount !== currentValues.discount ||
              prevValues.userTypeFrom !== currentValues.userTypeFrom ||
              prevValues.planTypeTo !== currentValues.planTypeTo ||
              prevValues.feature !== currentValues.feature
            }
          >
            {({ getFieldValue }) => {
              const discount = getFieldValue("discount");
              const userTypeFrom = getFieldValue("userTypeFrom");
              const planTypeTo = getFieldValue("planTypeTo");
              const feature = getFieldValue("feature");

              const hasTarget = isFeatureItem ? !!feature : !!planTypeTo;
              if (!discount || discount <= 0 || !userTypeFrom || !hasTarget)
                return null;

              const basePrice = parseFloat(calculatedBasePrice) || 0;
              const discountedPrice = basePrice - (basePrice * discount) / 100;

              return (
                <div
                  style={{
                    background: "#ecfdf5",
                    border: "1px solid #10b981",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 24,
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Typography.Text
                        style={{ color: "#059669", fontWeight: 500 }}
                      >
                        Discounted Price:
                      </Typography.Text>
                    </Col>
                    <Col>
                      <Space>
                        <Typography.Text
                          style={{
                            color: "#9ca3af",
                            textDecoration: "line-through",
                            fontSize: 14,
                          }}
                        >
                          ₹{basePrice.toFixed(2)}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            color: "#059669",
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          ₹{discountedPrice.toFixed(2)}
                        </Typography.Text>
                        <Tag color="green" style={{ margin: 0 }}>
                          {discount}% OFF
                        </Tag>
                      </Space>
                    </Col>
                  </Row>
                </div>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={isLifeTimeValidity ? 24 : 12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>
                    ONE TIME FEE
                  </span>
                }
                name="oneTimeFee"
                rules={[
                  { required: true, message: "Please input one time fee!" },
                ]}
                style={{ marginBottom: 24 }}
              >
                <InputNumber
                  size="large"
                  placeholder="e.g. 25000"
                  min={0}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              </Form.Item>
            </Col>

            {!isLifeTimeValidity && (
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      SUBSCRIPTION FEE
                    </span>
                  }
                  name="subscriptionFee"
                  rules={[
                    {
                      required: true,
                      message: "Please input subscription fee!",
                    },
                  ]}
                  style={{ marginBottom: 24 }}
                >
                  <InputNumber
                    size="large"
                    placeholder="e.g. 10000"
                    min={0}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {selectedItem && (
            <Form.Item
              label={
                <span style={{ fontWeight: 600, color: "#6b7280" }}>
                  STATUS
                </span>
              }
              name="status"
              rules={[{ required: true, message: "Please select status!" }]}
              style={{ marginBottom: 24, display: "none" }}
            >
              <Select
                size="large"
                placeholder="Select Status"
                style={{ borderRadius: 8 }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="INACTIVE">Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}

          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Item
              name="overridePaymentGateway"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox />
            </Form.Item>
            <div style={{ marginLeft: 10 }}>
              <span>Override Payment Gateway logic</span>
              <Tooltip title="Restrict which payment gateway can be used for this item.">
                <InfoCircleOutlined
                  style={{ color: "#9ca3af", marginLeft: 10 }}
                />
              </Tooltip>
            </div>
          </div>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.overridePaymentGateway !== curr.overridePaymentGateway ||
              prev.defaultPaymentGateway !== curr.defaultPaymentGateway ||
              prev.noCostEmiEnabled !== curr.noCostEmiEnabled
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("overridePaymentGateway") ? (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 16,
                    marginTop: 12,
                    marginBottom: 24,
                  }}
                >
                  <Form.Item
                    name="defaultPaymentGateway"
                    rules={[
                      {
                        required: true,
                        message: "Please select a payment gateway",
                      },
                    ]}
                    style={{ marginBottom: 12 }}
                  >
                    <Radio.Group>
                      <Radio value="indian">Indian (Razorpay/Paytm)</Radio>
                      <Radio
                        value="international"
                        disabled={!!getFieldValue("noCostEmiEnabled")}
                      >
                        International (Stripe)
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  <div
                    style={{
                      background: "#fef3c7",
                      border: "1px solid #fbbf24",
                      borderRadius: 8,
                      padding: 12,
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <InfoCircleOutlined
                      style={{ color: "#f59e0b", marginTop: 3 }}
                    />
                    <span style={{ fontSize: 13 }}>
                      {getFieldValue("defaultPaymentGateway") ===
                      "international"
                        ? "Enabling this allows purchases only with international payment options."
                        : "Enabling this allows purchases only with Indian payment options and a valid Indian phone number."}
                    </span>
                  </div>
                </div>
              ) : null
            }
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.overridePaymentGateway !== curr.overridePaymentGateway ||
              prev.defaultPaymentGateway !== curr.defaultPaymentGateway
            }
          >
            {({ getFieldValue }) => {
              const emiDisabled =
                !!getFieldValue("overridePaymentGateway") &&
                getFieldValue("defaultPaymentGateway") === "international";
              return (
                <div style={{ display: "flex", marginBottom: 24 }}>
                  <Form.Item
                    name="noCostEmiEnabled"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox disabled={emiDisabled} />
                  </Form.Item>
                  <div style={{ marginLeft: 10, position: "relative", top: 4 }}>
                    <span style={emiDisabled ? { color: "#9ca3af" } : {}}>
                      Enable No Cost EMI
                    </span>
                    <Tooltip
                      title={
                        emiDisabled
                          ? "No Cost EMI is not available for the International payment gateway."
                          : "Enable No Cost EMI for this item."
                      }
                    >
                      <InfoCircleOutlined
                        style={{ color: "#9ca3af", marginLeft: 10 }}
                      />
                    </Tooltip>
                  </div>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: "flex" }}>
            <Form.Item
              name="applicableForRefer"
              valuePropName="checked"
              style={{ marginBottom: 24 }}
            >
              <Checkbox />
            </Form.Item>
            <div style={{ marginLeft: 10, position: "relative", top: 4 }}>
              <span>Eligible for Refer & Earn</span>
            </div>
          </div>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.applicableForRefer !== currentValues.applicableForRefer
            }
            style={{ marginBottom: 24 }}
          >
            {({ getFieldValue }) =>
              getFieldValue("applicableForRefer") ? (
                <div
                  style={{
                    background: "#fef3c7",
                    border: "1px solid #fbbf24",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 24,
                  }}
                >
                  <Form.Item
                    label={
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#f59e0b",
                          fontSize: 12,
                        }}
                      >
                        REVENUE SHARE (%)
                      </span>
                    }
                    name="revShare"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          ) {
                            return Promise.reject(
                              new Error("Please input revenue share!")
                            );
                          }
                          if (value > 100) {
                            return Promise.reject(
                              new Error(
                                "Revenue share cannot be greater than 100%"
                              )
                            );
                          }
                          if (value < 0) {
                            return Promise.reject(
                              new Error("Revenue share cannot be negative")
                            );
                          }
                          const decimals = value.toString().split(".")[1];
                          if (decimals && decimals.length > 2) {
                            return Promise.reject(
                              new Error("Up to 2 decimal places allowed")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      size="large"
                      placeholder="0"
                      min={0}
                      max={100}
                      step={0.01}
                      precision={2}
                      style={{ width: "100%", borderRadius: 8 }}
                      addonAfter="%"
                    />
                  </Form.Item>

                  <div style={{ display: "flex", marginTop: 16 }}>
                    <Form.Item
                      name="showInRefernEarnDashboard"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox />
                    </Form.Item>
                    <div
                      style={{ marginLeft: 10, position: "relative", top: 4 }}
                    >
                      <span>Show in Refer &amp; Earn Dashboard</span>
                    </div>
                  </div>
                </div>
              ) : null
            }
          </Form.Item>

          <Form.Item style={{ marginBottom: 24, marginTop: 32 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={handleModalCancel}
                disabled={submitLoading}
                style={{
                  borderRadius: 8,
                  minWidth: 120,
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitLoading}
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 8,
                  minWidth: 160,
                }}
              >
                {selectedItem ? "Update Item" : "Add Item"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* No Configured Mango Modal */}
      <Modal
        title="Deleted Mango"
        open={showNoMangoModal}
        onCancel={() => setShowNoMangoModal(false)}
        centered
        zIndex={9999999}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setShowNoMangoModal(false)}
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
          >
            Okay
          </Button>,
        ]}
      >
        <Typography.Text>
          The Mango linked to this plan has been deleted. Please configure a
          valid Mango before creating this item.
        </Typography.Text>
      </Modal>

      {/* Multi-config picker modal */}
      <Modal
        title="Select Mango Configuration"
        open={showConfigPicker}
        onCancel={handleCancelConfigPicker}
        centered
        zIndex={9999999}
        footer={[
          <Button key="cancel" onClick={handleCancelConfigPicker}>
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={!pickedConfigId}
            onClick={handleConfirmConfigPicker}
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
          >
            Use This Configuration
          </Button>,
        ]}
      >
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: 12 }}
        >
          Multiple mango configurations match this selection. Pick the one to
          use for this sale item.
        </Typography.Text>
        <Radio.Group
          value={pickedConfigId}
          onChange={(e) => setPickedConfigId(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            {mangoConfigOptions.map((cfg) => (
              <Radio
                key={cfg._id}
                value={cfg._id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 12,
                  width: "100%",
                  marginRight: 0,
                }}
              >
                <div style={{ marginLeft: 4 }}>
                  <Typography.Text strong>
                    {cfg.mango?.title || "Untitled Mango"}
                  </Typography.Text>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Base Price: ₹{Number(cfg.mango?.inrAmount || 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Mango ID: {cfg.mango?._id || "-"}
                  </div>
                  {cfg.features?.length ? (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Features: {cfg.features.join(", ")}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Config ID: {cfg._id}
                  </div>
                </div>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>
    </>
  );
};

export default ManageSale;
